import shortHash from 'short-hash'

export function useCheckOffer() {
  const cart = useCart()
  const dayjs = useDayjs()

  type base = {
    id: string | null,
    autoApply: boolean | null,
    percent: Number | null,
    remainingAmount: Number | null,
    providerUkid: string | null
  }

  type DeliveryOffer = base & {
    type: 'delivery'
  }
  type DiscountOffer = base & {
    discountType: string
    discount: number
    discountCap: number
    type: 'discount'
  }

  type ComboOffer = {
    id: string
    autoApply: boolean
    comboItems: string[]
    discountType: string
    discount: number
    discountCap: number
    type: 'combo'
  }
  
  type FreebieOffer = base & {
    itemCount: number
    sku_id: string
    type: 'freebie'
  }
  
  type SlabOffer = {
    sku_id: string
    slabs: {
      id: string
      sku_id: string
      current_qty: number
      discountPercent: number | null
      discountAmount: number | null
      discountCap: number
      discountedPrice: number
      actualDiscount: number
      minQty: number
      maxQty: number | null
      providerUkid: string
      autoApply: boolean
      type: 'slab'
    }[]
  }
  
  type OfferResult = {
    delivery: DeliveryOffer[]
    discount: DiscountOffer[]
    freebie: FreebieOffer[]
    slab: SlabOffer[]
    combo: ComboOffer[]
  }

  const offerResult : OfferResult = {
    delivery: [],
    discount: [],
    freebie: [],
    slab: [],
    combo: []
  }
  if (!cart.sellerOffer.offers?.length) return offerResult

  const slabGroups: Record<string, any[]> = {}

  for (const entry of cart.sellerOffer.offers) {
    const matchedItems = cart.items.filter(
      item =>
        item.provider.ukid === entry.ukid &&
        (!entry.sku_id || item.sku_id === entry.sku_id)
    )
    if (!matchedItems.length) continue

    const totalPrice = matchedItems.reduce((sum, i) => sum + i.price * i.qty, 0)
    const providerUkid = matchedItems[0]?.provider?.ukid ?? ''
    let hasNonAdditiveSlab = false

    for (const offer of entry.offers || []) {
      const {
        id,
        code = '',
        meta = {},
        benefit = {},
        qualifier = {},
        valid_from,
        valid_upto,
        location_ids = [],
        category_ids = []
      } = offer

      const offerCode = code.toLowerCase()
      const additive = meta.additive
      const autoApply = meta.auto
      const now = dayjs()

      // validity check
      if (valid_from && valid_upto) {
        if (!(now.isAfter(dayjs(valid_from)) && now.isBefore(dayjs(valid_upto)))) continue
      }

      const [item] = matchedItems
      if (
        (location_ids.length && !location_ids.includes(item.location.id)) ||
        (category_ids.length && !category_ids.includes(item.category.parent?.code))
      ) continue

      const minValue = Number(qualifier.min_value ?? 0)
      const percent = Math.min(Math.round((totalPrice / minValue) * 100), 100)
      const remainingAmount = percent < 100 ? Math.max(0, minValue - totalPrice) : 0
      const base = { id, autoApply, percent, remainingAmount, providerUkid }
      
      if (offerCode === 'discount') {
        const discount = { 
          ...base,
          qualifier: qualifier,
          discountType: benefit.value_type || 'amount',
          discount: benefit.value ?? 0,
          discountCap: benefit.value_cap ?? 0,
          type: 'discount' 
        } as DiscountOffer
        if (additive === 'no') return { delivery: [], discount: [discount], freebie: [], slab: [], combo: [] }
        offerResult.discount.push(discount)
        continue
      }

      if (offerCode === 'delivery') {
        const delivery = { ...base, type: 'delivery' } as DeliveryOffer
        if (additive === 'no') return { delivery: [delivery], discount: [], freebie: [], slab: [], combo: [] }
        offerResult.delivery.push(delivery)
        continue
      }

      if (offerCode === 'combo') {
        const comboItems = (qualifier.item_id || '')
          .split(',')
          .filter(Boolean)
          .map((code: any) =>
            'SKU' +
            shortHash(`${item.seller?.id}|${item.provider?.id}|${code}`).toUpperCase()
          )

        const combo = {
          id,
          autoApply,
          comboItems,
          discountType: benefit.value_type || 'amount',
          discount: benefit.value ?? 0,
          discountCap: benefit.value_cap ?? 0,
          type: 'combo'
        } as ComboOffer

        if (additive === 'no') return { delivery: [], discount: [], freebie: [], slab: [], combo: [combo] }
        offerResult.combo.push(combo)
        continue
      }

      if (offerCode === 'freebie') {
        const sku_id = 'SKU' + shortHash(`${item.seller?.id}|${item.provider?.id}|${benefit.item_id}`).toUpperCase()
        const freebie = {
          ...base,
          itemCount: Number(benefit.item_count ?? 0),
          sku_id,
          type: 'freebie'
        } as FreebieOffer
        if (additive === 'no') return { delivery: [], discount: [], freebie: [freebie], slab: [], combo: [] }
        offerResult.freebie.push(freebie)
        continue
      }

      if (offerCode === 'slab') {
        const sku_id = entry.sku_id
        const skuMatched = matchedItems.filter(i => i.sku_id === sku_id)
        if (!skuMatched.length) continue

        const skuQty = skuMatched.reduce((sum, i) => sum + i.qty, 0)
        const skuTotal = skuMatched.reduce((sum, i) => sum + i.price * i.qty, 0)
        if (skuQty === 0) continue

        const value = Math.abs(Number(benefit.value ?? 0))
        const cap = Math.abs(Number(benefit.value_cap ?? 0))
        const valueType = benefit.value_type?.toLowerCase() ?? 'amount'
        const discount = valueType === 'percent'
          ? Math.min((value / 100) * skuTotal, cap)
          : Math.min(value, cap)

        const slabEntry = {
          id,
          sku_id,
          current_qty: skuQty,
          discountPercent: valueType === 'percent' ? value : null,
          discountAmount: valueType === 'amount' ? value : null,
          discountCap: cap,
          discountedPrice: Math.round(skuTotal - discount),
          actualDiscount: Math.round(discount),
          minQty: Number(qualifier.item_count ?? 0),
          maxQty: qualifier.item_count_upper ? Number(qualifier.item_count_upper) : null,
          providerUkid,
          autoApply,
          type: 'slab'
        }

        if (!slabGroups[sku_id]) slabGroups[sku_id] = []
        slabGroups[sku_id].push(slabEntry)
        if (additive === 'no') hasNonAdditiveSlab = true
      }
    }

    if (hasNonAdditiveSlab) {
      return {
        delivery: [],
        discount: [],
        freebie: [],
        slab: Object.values(slabGroups).map(slabs => ({ sku_id: slabs[0].sku_id, slabs })),
        combo: []
      }
    }
  }

  offerResult.slab = Object.values(slabGroups).map(slabs => ({ sku_id: slabs[0].sku_id, slabs }))
  console.log("offer data ", offerResult)
  return offerResult
}
